<template>
    <main class="g10c" dir="rtl" lang="ar">
        <nav id="breadcrumbs" class="g10c-breadcrumbs g10c-wrap" aria-label="مسار الصفحة">
            <ol class="g10c-breadcrumbs__list">
                <li>
                    <router-link :to="config.homeRoute" @click.native="onInternal('home', 'breadcrumb')">
                        الرئيسية
                    </router-link>
                </li>
                <li>
                    <router-link
                        :to="config.grade10LandingRoute"
                        @click.native="onTrack('grade10_grade_landing_click', 'breadcrumb', config.grade10LandingUrl)"
                    >
                        أولى ثانوي
                    </router-link>
                </li>
                <li aria-current="page">منهج البرمجة والذكاء الاصطناعي</li>
            </ol>
        </nav>

        <!-- Hero -->
        <section id="hero" class="g10c-hero">
            <div class="g10c-wrap g10c-hero__grid">
                <div class="g10c-hero__content">
                    <span class="g10c-badge">منهج أولى ثانوي · {{ config.officialAcademicYear }}</span>
                    <h1 class="g10c-hero__h1">منهج البرمجة والذكاء الاصطناعي أولى ثانوي</h1>
                    <p class="g10c-hero__lead">
                        الكتاب كامل، توزيع الترمين، والدروس اللي اتدرست فعلًا
                    </p>
                    <p class="g10c-hero__text">
                        منهج البرمجة أولى ثانوي مش مجرد كتابة كود. الطالب بيتعرف من خلاله على المعلومات، والأمان الرقمي، والأنظمة الثنائية، والشبكات، وقواعد البيانات، وتحليل البيانات، والخوارزميات، والبرمجة، والذكاء الاصطناعي التوليدي، وبناء صفحات ومشروعات ويب.
                        في الصفحة دي هتقدر تعرف المنهج الكامل الموجود في الكتاب، وإيه اللي تم تدريسه في الترم الأول والترم الثاني، وإيه الموجود في الكتاب لكنه غير مدرج في التوزيع الرسمي المتاح
                        {{ config.officialAcademicYear }}.
                    </p>
                    <div class="g10c-hero__chips">
                        <span v-for="chip in heroChips" :key="chip" class="g10c-chip">
                            {{ chip }}
                        </span>
                    </div>
                </div>

                <div class="g10c-hero__visual">
                    <div
                        class="g10c-hero-map"
                        role="img"
                        aria-label="مسار مفاهيم منهج البرمجة أولى ثانوي"
                    >
                        <div
                            v-for="area in heroFlow"
                            :key="area.label"
                            class="g10c-hero-map__card"
                        >
                            <lucide-icon :name="area.icon" :size="22" aria-hidden="true" />
                            <span class="ltr-inline">{{ area.label }}</span>
                        </div>
                    </div>
                </div>

                <div class="g10c-cta-pair g10c-hero__ctas">
                    <div class="g10c-cta-card g10c-cta-card--primary">
                        <div class="g10c-cta-card__head">
                            <div class="g10c-cta-card__icon" aria-hidden="true">
                                <lucide-icon name="Download" :size="22" />
                            </div>
                            <div>
                                <p class="g10c-cta-card__eyebrow">مجاني</p>
                                <p class="g10c-cta-card__title">تحميل الكتاب الرسمي</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            class="g10c-btn g10c-btn--primary"
                            @click="openDownloadSheet('hero')"
                        >
                            <lucide-icon name="BookOpen" :size="20" aria-hidden="true" />
                            <span class="g10c-btn__label g10c-btn__label--full">حمّل الكتاب العربي أو English</span>
                            <span class="g10c-btn__label g10c-btn__label--short">حمّل الكتاب</span>
                        </button>
                    </div>
                    <div class="g10c-cta-card g10c-cta-card--secondary">
                        <div class="g10c-cta-card__head">
                            <div class="g10c-cta-card__icon g10c-cta-card__icon--dark" aria-hidden="true">
                                <lucide-icon name="Rocket" :size="22" />
                            </div>
                            <div>
                                <p class="g10c-cta-card__eyebrow">ابدأ الدراسة</p>
                                <p class="g10c-cta-card__title">كورس أولى ثانوي</p>
                            </div>
                        </div>
                        <router-link
                            :to="config.grade10TermOneRoute"
                            class="g10c-btn g10c-btn--secondary"
                            @click.native="onTrack('grade10_course_cta_click', 'hero', config.grade10TermOneUrl)"
                        >
                            <lucide-icon name="PlayCircle" :size="20" aria-hidden="true" />
                            <span class="g10c-btn__label g10c-btn__label--full">ابدأ كورس أولى ثانوي</span>
                            <span class="g10c-btn__label g10c-btn__label--short">ابدأ الكورس</span>
                        </router-link>
                    </div>
                </div>
            </div>
        </section>

        <!-- Update notice -->
        <section id="update-notice" class="g10c-section g10c-section--notice">
            <div class="g10c-wrap">
                <aside class="g10c-notice" role="note">
                    <div class="g10c-notice__icon" aria-hidden="true">
                        <lucide-icon name="AlertTriangle" :size="22" />
                    </div>
                    <div>
                        <h2 class="g10c-notice__title">تنويه مهم عن العام الدراسي</h2>
                        <p class="g10c-text">
                            المواد الرسمية المتاحة حاليًا (الكتاب والتوزيع) تخص العام الدراسي
                            <strong>{{ config.officialAcademicYear }}</strong>.
                            هذه الصفحة مرجع دقيق لما دُرّس وما وُجد في الكتاب حسب تلك المصادر، وليست إعلانًا رسميًا عن منهج
                            {{ config.currentAcademicYear }} قبل صدور الملفات الجديدة.
                        </p>
                        <p class="g10c-text g10c-text--strong">
                            آخر مراجعة للمحتوى: {{ formattedReviewDate }}
                        </p>
                    </div>
                </aside>
            </div>
        </section>

        <!-- Downloads -->
        <section id="downloads" class="g10c-section g10c-section--soft">
            <div class="g10c-wrap">
                <h2 class="g10c-h2 g10c-h2--center">تحميل كتاب البرمجة أولى ثانوي PDF</h2>
                <p class="g10c-lead g10c-lead--center">
                    نسخة عربية ونسخة English للعام الدراسي {{ config.officialAcademicYear }}.
                </p>
                <div class="g10c-download-grid">
                    <article
                        v-for="book in downloadBooks"
                        :key="book.language"
                        class="g10c-download-card"
                        :class="{ 'g10c-download-card--en': book.language === 'en' }"
                    >
                        <div class="g10c-download-card__head">
                            <lucide-icon
                                :name="book.language === 'ar' ? 'BookMarked' : 'Languages'"
                                :size="28"
                                aria-hidden="true"
                            />
                            <h3 class="g10c-download-card__title" :class="{ 'ltr-inline': book.language === 'en' }">
                                {{ book.title }}
                            </h3>
                        </div>
                        <ul class="g10c-download-card__meta">
                            <li v-for="detail in book.details" :key="detail">{{ detail }}</li>
                            <li>{{ book.fileSize }}</li>
                        </ul>
                        <div class="g10c-download-card__actions">
                            <a
                                :href="book.url"
                                class="g10c-btn g10c-btn--primary"
                                :download="downloadFileName(book)"
                                @click="onBookTrack(book.trackDownload, 'downloads', book.url)"
                            >
                                <lucide-icon name="Download" :size="18" aria-hidden="true" />
                                <span>{{ book.downloadLabel }}</span>
                            </a>
                            <a
                                :href="book.url"
                                class="g10c-btn g10c-btn--ghost"
                                target="_blank"
                                rel="noopener noreferrer"
                                @click="onBookTrack(book.trackOpen, 'downloads', book.url)"
                            >
                                <lucide-icon name="ExternalLink" :size="18" aria-hidden="true" />
                                <span>{{ book.openLabel }}</span>
                            </a>
                        </div>
                    </article>
                </div>
            </div>
        </section>

        <!-- Overview -->
        <section id="overview" class="g10c-section">
            <div class="g10c-wrap g10c-section__narrow">
                <h2 class="g10c-h2">ما هو منهج البرمجة أولى ثانوي؟</h2>
                <p class="g10c-text">
                    منهج البرمجة والذكاء الاصطناعي للصف الأول الثانوي يجمع بين فهم المعلومات والأمان الرقمي،
                    وتمثيل البيانات، ومكونات الكمبيوتر، والشبكات، وقواعد البيانات، والخوارزميات، والبرمجة العملية،
                    وصولًا إلى
                    <span class="ltr-inline">Generative AI</span>
                    ومشروعات الويب.
                </p>
                <p class="g10c-text">
                    الكتاب الكامل يحتوي على {{ config.unitCount }} وحدة. التوزيع الرسمي يحدد ما يُدرَّس فعليًا في كل ترم،
                    وقد تبقى دروس داخل الكتاب دون إدراج في التوزيع.
                </p>
            </div>
            <div class="g10c-wrap">
                <div class="g10c-summary-grid">
                    <div v-for="item in summaryGrid" :key="item.label" class="g10c-summary-card">
                        <lucide-icon :name="item.icon" :size="22" aria-hidden="true" />
                        <span>{{ item.label }}</span>
                    </div>
                </div>
            </div>
        </section>

        <!-- Status guide -->
        <section id="status-guide" class="g10c-section g10c-section--soft">
            <div class="g10c-wrap">
                <h2 class="g10c-h2">دليل حالات الدروس</h2>
                <p class="g10c-text">
                    نستخدم تسميات واضحة بدل كلمة «محذوف»، لأن الدرس قد يبقى في الكتاب دون أن يظهر في التوزيع الرسمي.
                </p>
                <div class="g10c-status-guide">
                    <article
                        v-for="item in statusGuide"
                        :key="item.id"
                        class="g10c-status-guide__card"
                        :class="`g10c-status-guide__card--${item.color}`"
                    >
                        <span class="g10c-status" :class="`g10c-status--${item.id}`">{{ item.title }}</span>
                        <p>{{ item.meaning }}</p>
                        <p v-if="item.note" class="g10c-status-guide__note">{{ item.note }}</p>
                    </article>
                </div>
            </div>
        </section>

        <!-- Filters + units map -->
        <section id="filters" class="g10c-section">
            <div class="g10c-wrap">
                <h2 class="g10c-h2 g10c-h2--center">خريطة وحدات المنهج</h2>
                <p class="g10c-lead g10c-lead--center">
                    {{ config.unitCount }} وحدة — صفِّ حسب الترم أو نوع المحتوى. المحتوى يبقى في الصفحة لمحركات البحث.
                </p>

                <div class="g10c-filters" role="toolbar" aria-label="تصفية وحدات المنهج">
                    <button
                        v-for="option in filterOptions"
                        :key="option.id"
                        type="button"
                        class="g10c-filter"
                        :class="{ 'g10c-filter--active': activeFilterId === option.id }"
                        :aria-pressed="String(activeFilterId === option.id)"
                        @click="setFilter(option)"
                    >
                        {{ option.label }}
                    </button>
                </div>

                <nav class="g10c-toc" aria-label="وحدات المنهج">
                    <div class="g10c-toc__header">
                        <div class="g10c-toc__header-icon" aria-hidden="true">
                            <lucide-icon name="ListTree" :size="22" />
                        </div>
                        <div>
                            <p class="g10c-toc__title">انتقل إلى وحدة</p>
                            <p class="g10c-toc__subtitle">اضغط للانتقال مباشرة إلى أي وحدة</p>
                        </div>
                    </div>
                    <div class="g10c-toc__grid">
                        <a
                            v-for="unit in units"
                            :key="`toc-${unit.id}`"
                            :href="`#${unit.id}`"
                            class="g10c-toc__card"
                            v-show="unitMatchesFilter(unit)"
                            @click="onUnitNav(unit)"
                        >
                            <span class="g10c-toc__num">{{ unit.number }}</span>
                            <span class="g10c-toc__icon" aria-hidden="true">
                                <lucide-icon :name="unit.icon" :size="20" />
                            </span>
                            <span class="g10c-toc__text">
                                <span class="g10c-toc__name">{{ unit.arabicTitle }}</span>
                                <span class="g10c-toc__en ltr-inline">{{ unit.englishTitle }}</span>
                            </span>
                        </a>
                    </div>
                </nav>

                <div id="units" class="g10c-units">
                    <curriculum-unit-block
                        v-for="(unit, index) in units"
                        :key="unit.id"
                        :unit="unit"
                        :unit-visible="unitMatchesFilter(unit)"
                        :lesson-matcher="lessonMatchesFilter"
                        :initially-open="index === 0 && activeFilterId === 'all'"
                        @open="onUnitOpen"
                    />
                </div>
            </div>
        </section>

        <!-- Term 1 -->
        <section id="term-1" class="g10c-section g10c-section--soft">
            <div class="g10c-wrap">
                <h2 class="g10c-h2">ما دُرّس في الترم الأول؟</h2>
                <p class="g10c-text">{{ termOneSummary }}</p>
                <figure
                    v-if="termOneMap"
                    class="g10c-term-map"
                    itemscope
                    itemtype="https://schema.org/ImageObject"
                >
                    <button
                        type="button"
                        class="g10c-term-map__trigger"
                        :aria-label="`تكبير ${termOneMap.shortLabel}`"
                        @click="openSourceModal(termOneMap)"
                    >
                        <img
                            :src="termOneMap.src"
                            :alt="termOneMap.alt"
                            :title="termOneMap.title"
                            :width="termOneMap.width"
                            :height="termOneMap.height"
                            loading="lazy"
                            decoding="async"
                            class="g10c-term-map__img"
                            itemprop="contentUrl"
                        />
                    </button>
                    <figcaption class="g10c-term-map__caption" itemprop="caption">
                        {{ termOneMap.caption }}
                    </figcaption>
                    <meta itemprop="name" :content="termOneMap.label" />
                    <meta itemprop="description" :content="termOneMap.alt" />
                </figure>
                <div class="g10c-weeks">
                    <article
                        v-for="week in termOneWeeks"
                        :key="`t1-${week.week}`"
                        class="g10c-week"
                        :class="{ 'g10c-week--final': week.isFinalPeriod }"
                    >
                        <h3 class="g10c-week__title">{{ week.label }}</h3>
                        <ul v-if="week.bookLessons && week.bookLessons.length" class="g10c-week__list">
                            <li v-for="(lesson, i) in week.bookLessons" :key="`t1-book-${week.week}-${i}`">
                                <span v-if="lesson.code" class="ltr-inline g10c-week__code">{{ lesson.code }}</span>
                                {{ lesson.title }}
                            </li>
                        </ul>
                        <ul v-if="week.qureoItems && week.qureoItems.length" class="g10c-week__list g10c-week__list--qureo">
                            <li v-for="item in week.qureoItems" :key="`t1-q-${week.week}-${item}`">
                                <span class="ltr-inline">Qureo:</span> {{ item }}
                            </li>
                        </ul>
                        <ul v-if="week.platformItems && week.platformItems.length" class="g10c-week__list">
                            <li v-for="item in week.platformItems" :key="`t1-p-${week.week}-${item}`">{{ item }}</li>
                        </ul>
                        <p v-if="week.notes" class="g10c-week__notes">{{ week.notes }}</p>
                    </article>
                </div>
            </div>
        </section>

        <!-- Term 2 -->
        <section id="term-2" class="g10c-section">
            <div class="g10c-wrap">
                <h2 class="g10c-h2">ما دُرّس في الترم الثاني؟</h2>
                <p class="g10c-text">{{ termTwoSummary }}</p>
                <figure
                    v-if="termTwoMap"
                    class="g10c-term-map"
                    itemscope
                    itemtype="https://schema.org/ImageObject"
                >
                    <button
                        type="button"
                        class="g10c-term-map__trigger"
                        :aria-label="`تكبير ${termTwoMap.shortLabel}`"
                        @click="openSourceModal(termTwoMap)"
                    >
                        <img
                            :src="termTwoMap.src"
                            :alt="termTwoMap.alt"
                            :title="termTwoMap.title"
                            :width="termTwoMap.width"
                            :height="termTwoMap.height"
                            loading="lazy"
                            decoding="async"
                            class="g10c-term-map__img"
                            itemprop="contentUrl"
                        />
                    </button>
                    <figcaption class="g10c-term-map__caption" itemprop="caption">
                        {{ termTwoMap.caption }}
                    </figcaption>
                    <meta itemprop="name" :content="termTwoMap.label" />
                    <meta itemprop="description" :content="termTwoMap.alt" />
                </figure>
                <div class="g10c-weeks">
                    <article
                        v-for="week in termTwoWeeks"
                        :key="`t2-${week.week}`"
                        class="g10c-week"
                        :class="{ 'g10c-week--final': week.isFinalPeriod }"
                    >
                        <h3 class="g10c-week__title">{{ week.label }}</h3>
                        <ul v-if="week.bookLessons && week.bookLessons.length" class="g10c-week__list">
                            <li v-for="(lesson, i) in week.bookLessons" :key="`t2-book-${week.week}-${i}`">
                                <span v-if="lesson.code" class="ltr-inline g10c-week__code">{{ lesson.code }}</span>
                                {{ lesson.title }}
                            </li>
                        </ul>
                        <ul v-if="week.qureoItems && week.qureoItems.length" class="g10c-week__list g10c-week__list--qureo">
                            <li v-for="item in week.qureoItems" :key="`t2-q-${week.week}-${item}`">
                                <span class="ltr-inline">Qureo:</span> {{ item }}
                            </li>
                        </ul>
                        <ul v-if="week.platformItems && week.platformItems.length" class="g10c-week__list">
                            <li v-for="item in week.platformItems" :key="`t2-p-${week.week}-${item}`">{{ item }}</li>
                        </ul>
                        <p v-if="week.notes" class="g10c-week__notes">{{ week.notes }}</p>
                    </article>
                </div>
            </div>
        </section>

        <!-- Not scheduled -->
        <section id="not-scheduled" class="g10c-section g10c-section--soft">
            <div class="g10c-wrap">
                <h2 class="g10c-h2">موجود في الكتاب وغير مدرج في التوزيع</h2>
                <p class="g10c-text">{{ notScheduledByTerm.warning }}</p>
                <div class="g10c-split">
                    <div class="g10c-split__card">
                        <h3>الترم الأول</h3>
                        <ul class="g10c-checklist">
                            <li v-for="item in notScheduledByTerm.term1" :key="`ns1-${item.code}`">
                                <span v-if="!item.isWholeUnit" class="ltr-inline">{{ item.code }} — </span>
                                {{ item.arabicTitle }}
                                <span class="ltr-inline g10c-muted"> ({{ item.englishTitle }})</span>
                            </li>
                        </ul>
                    </div>
                    <div class="g10c-split__card g10c-split__card--accent">
                        <h3>الترم الثاني</h3>
                        <ul class="g10c-checklist">
                            <li v-for="item in notScheduledByTerm.term2" :key="`ns2-${item.code}`">
                                <span v-if="!item.isWholeUnit" class="ltr-inline">{{ item.code }} — </span>
                                {{ item.arabicTitle }}
                                <span class="ltr-inline g10c-muted"> ({{ item.englishTitle }})</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>

        <!-- Sources -->
        <section id="sources" class="g10c-section">
            <div class="g10c-wrap">
                <h2 class="g10c-h2">المصادر الرسمية المعتمدة في الصفحة</h2>
                <p class="g10c-text">
                    خرائط توزيع منهج البرمجة والذكاء الاصطناعي أولى ثانوي للترم الأول والترم الثاني
                    {{ config.officialAcademicYear }} من Coding With Ghonaim، مع أغلفة الكتب الرسمية عند توفرها.
                </p>
                <div class="g10c-sources">
                    <button
                        v-for="doc in sourceDocuments"
                        :key="doc.id"
                        type="button"
                        class="g10c-source"
                        :class="{
                            'g10c-source--pending': doc.pending,
                            'g10c-source--map': doc.type === 'curriculum-map',
                        }"
                        :disabled="doc.pending"
                        @click="openSourceModal(doc)"
                    >
                        <span v-if="doc.pending" class="g10c-source__pending">قريبًا</span>
                        <img
                            v-else
                            :src="doc.src"
                            :alt="doc.alt"
                            :title="doc.title || doc.label"
                            :width="doc.width || undefined"
                            :height="doc.height || undefined"
                            loading="lazy"
                            decoding="async"
                            class="g10c-source__img"
                        />
                        <span class="g10c-source__label">{{ doc.shortLabel || doc.label }}</span>
                    </button>
                </div>
            </div>
        </section>

        <!-- Arabic vs English -->
        <section id="arabic-english" class="g10c-section g10c-section--soft">
            <div class="g10c-wrap">
                <h2 class="g10c-h2">عربي أم لغات؟</h2>
                <div class="g10c-split">
                    <div class="g10c-split__card">
                        <h3>النسخة العربية</h3>
                        <p>مناسبة لطلاب المدارس العربية، مع شرح ومصطلحات بالعربية وربط بالمفاهيم الإنجليزية الأساسية.</p>
                    </div>
                    <div class="g10c-split__card g10c-split__card--accent">
                        <h3 class="ltr-inline">English Book</h3>
                        <p>مناسبة لطلاب مدارس اللغات، بنفس البناء العام للوحدات والمشروعات مع مصطلحات إنجليزية أوضح.</p>
                    </div>
                </div>
                <p class="g10c-text g10c-text--strong">
                    اختر نسخة الكتاب المناسبة لنظام مدرستك، وراجع التوزيع قبل التركيز على أي درس.
                </p>
            </div>
        </section>

        <!-- Theory vs practical -->
        <section id="theory-practical" class="g10c-section">
            <div class="g10c-wrap">
                <h2 class="g10c-h2">نظري وعملي في منهج أولى ثانوي</h2>
                <div class="g10c-theory-grid">
                    <article v-for="col in theoryColumns" :key="col.id" class="g10c-theory-card">
                        <div class="g10c-theory-card__head">
                            <lucide-icon :name="col.icon" :size="24" aria-hidden="true" />
                            <h3>{{ col.title }}</h3>
                        </div>
                        <ul>
                            <li v-for="item in col.items" :key="`${col.id}-${item}`" class="ltr-inline">{{ item }}</li>
                        </ul>
                    </article>
                </div>
            </div>
        </section>

        <!-- Benefits -->
        <section id="benefits" class="g10c-section g10c-section--soft">
            <div class="g10c-wrap">
                <h2 class="g10c-h2">ماذا سيستفيد الطالب؟</h2>
                <div class="g10c-cards g10c-cards--3">
                    <div v-for="benefit in benefits" :key="benefit" class="g10c-card">
                        <lucide-icon name="CheckCircle2" :size="22" class="g10c-card__icon" aria-hidden="true" />
                        <p>{{ benefit }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Study -->
        <section id="study" class="g10c-section">
            <div class="g10c-wrap">
                <h2 class="g10c-h2">كيف تذاكر منهج البرمجة أولى ثانوي؟</h2>
                <ol class="g10c-steps">
                    <li v-for="(step, index) in studySteps" :key="step">
                        <span class="g10c-steps__num">{{ index + 1 }}</span>
                        <span>{{ step }}</span>
                    </li>
                </ol>
            </div>
        </section>

        <!-- Course CTA -->
        <section id="course-cta" class="g10c-section g10c-section--cta-band">
            <div class="g10c-wrap g10c-section__narrow">
                <p class="g10c-kicker">جاهز تبدأ بشرح منظم؟</p>
                <h2 class="g10c-h2 g10c-h2--light">كورس البرمجة أولى ثانوي مع Coding With Ghonaim</h2>
                <p class="g10c-text g10c-text--light">
                    شرح عربي ولغات، تنظيم حسب التوزيع، تدريبات ومراجعات، وربط منهج أولى ثانوي بما سيحتاجه الطالب في تانية ثانوي.
                </p>
                <ul class="g10c-checklist g10c-checklist--light">
                    <li v-for="feature in courseFeatures" :key="feature">{{ feature }}</li>
                </ul>
                <router-link
                    :to="config.grade10TermOneRoute"
                    class="g10c-btn g10c-btn--primary g10c-btn--on-dark"
                    @click.native="onTrack('grade10_course_cta_click', 'course_cta', config.grade10TermOneUrl)"
                >
                    <lucide-icon name="Rocket" :size="20" aria-hidden="true" />
                    <span>ابدأ كورس أولى ثانوي</span>
                </router-link>
            </div>
        </section>

        <!-- Grade 11 next -->
        <section id="grade11-next" class="g10c-section">
            <div class="g10c-wrap g10c-section__narrow">
                <h2 class="g10c-h2">بعد أولى ثانوي… تانية ثانوي</h2>
                <p class="g10c-text">
                    منهج أولى ثانوي يؤسس للمفاهيم التي تُبنى عليها تانية ثانوي بكالوريا: الأنظمة الرقمية، الويب، البيانات، والذكاء الاصطناعي.
                </p>
                <router-link
                    :to="config.grade11CurriculumRoute"
                    class="g10c-btn g10c-btn--secondary"
                    @click.native="onTrack('grade10_grade11_curriculum_click', 'grade11_next', config.grade11CurriculumUrl)"
                >
                    <lucide-icon name="ArrowLeft" :size="20" aria-hidden="true" />
                    <span>تصفح منهج البرمجة تانية ثانوي</span>
                </router-link>
            </div>
        </section>

        <!-- Teacher -->
        <section id="teacher" class="g10c-section g10c-section--soft">
            <div class="g10c-wrap g10c-teacher">
                <div class="g10c-teacher__media">
                    <img
                        :src="teacherImage"
                        alt="كورس البرمجة أولى ثانوي مع محمد غنيم"
                        width="512"
                        height="640"
                        loading="lazy"
                        decoding="async"
                        class="g10c-teacher__img"
                    />
                </div>
                <div class="g10c-teacher__content">
                    <h2 class="g10c-h2">هل تبحث عن مدرس برمجة أولى ثانوي؟</h2>
                    <p class="g10c-text">
                        مع المهندس محمد غنيم، الطالب يدخل منظومة تساعده على فهم الدرس، التطبيق على
                        <span class="ltr-inline">Qureo</span>،
                        حل أسئلة الكتاب، والمراجعة قبل الاختبارات.
                    </p>
                    <ul class="g10c-checklist">
                        <li>شرح عربي ولغات.</li>
                        <li>تنظيم المحتوى حسب التوزيع.</li>
                        <li>تدريبات وQuizzes ومتابعة.</li>
                        <li>ربط أولى ثانوي بما يحتاجه في تانية ثانوي.</li>
                    </ul>
                    <div class="g10c-cta-pair">
                        <router-link
                            :to="config.aboutGhonaimRoute"
                            class="g10c-btn g10c-btn--ghost"
                            @click.native="onTrack('grade10_about_ghonaim_click', 'teacher', config.aboutGhonaimUrl)"
                        >
                            <lucide-icon name="User" :size="20" aria-hidden="true" />
                            <span>تعرف أكثر على محمد غنيم</span>
                        </router-link>
                        <router-link
                            :to="config.grade10TermOneRoute"
                            class="g10c-btn g10c-btn--primary"
                            @click.native="onTrack('grade10_course_cta_click', 'teacher', config.grade10TermOneUrl)"
                        >
                            <lucide-icon name="PlayCircle" :size="20" aria-hidden="true" />
                            <span>ابدأ الكورس</span>
                        </router-link>
                    </div>
                </div>
            </div>
        </section>

        <!-- FAQ -->
        <section id="faq" class="g10c-section">
            <div class="g10c-wrap g10c-section__narrow">
                <h2 class="g10c-h2">الأسئلة الشائعة</h2>
                <curriculum-faq-accordion :items="faqItems" @open="onFaqOpen" />
            </div>
        </section>

        <!-- Final CTA -->
        <section id="final-cta" class="g10c-section g10c-section--final">
            <div class="g10c-wrap g10c-section__narrow g10c-final">
                <p class="g10c-kicker g10c-kicker--on-final">منهج أولى ثانوي بوضوح</p>
                <h2 class="g10c-h2 g10c-h2--light">حمّل الكتاب أو ابدأ الكورس</h2>
                <p class="g10c-text g10c-text--light">
                    راجع الكتاب والتوزيع، ثم ابدأ الدراسة بخطة منظمة مع Coding With Ghonaim.
                </p>
                <div class="g10c-cta-pair g10c-cta-pair--center">
                    <button
                        type="button"
                        class="g10c-btn g10c-btn--outline-light"
                        @click="openDownloadSheet('final_cta')"
                    >
                        <lucide-icon name="Download" :size="20" aria-hidden="true" />
                        <span>حمّل الكتاب</span>
                    </button>
                    <router-link
                        :to="config.grade10TermOneRoute"
                        class="g10c-btn g10c-btn--primary g10c-btn--on-dark"
                        @click.native="onTrack('grade10_course_cta_click', 'final_cta', config.grade10TermOneUrl)"
                    >
                        <lucide-icon name="Rocket" :size="20" aria-hidden="true" />
                        <span>ابدأ كورس أولى ثانوي</span>
                    </router-link>
                </div>
                <p class="g10c-final__brand">
                    Coding With Ghonaim
                    <br />
                    بنفهم التكنولوجيا… مش بس بنستخدمها.
                </p>
            </div>
        </section>

        <curriculum-mobile-sticky
            :course-route="config.grade10TermOneRoute"
            :arabic-book-url="config.arabicBookUrl"
            :english-book-url="config.englishBookUrl"
            @download-open="openDownloadSheet('mobile_sticky')"
            @course-click="onTrack('grade10_course_cta_click', 'mobile_sticky', config.grade10TermOneUrl)"
        />

        <!-- Download sheet -->
        <div
            v-if="downloadSheetOpen"
            class="g10c-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="g10c-download-sheet-title"
            @keydown.esc.prevent="closeDownloadSheet"
        >
            <button type="button" class="g10c-sheet__backdrop" aria-label="إغلاق" @click="closeDownloadSheet" />
            <div class="g10c-sheet__panel" ref="downloadSheet">
                <div class="g10c-sheet__head">
                    <h2 id="g10c-download-sheet-title" class="g10c-sheet__title">اختر نسخة الكتاب</h2>
                    <button type="button" class="g10c-sheet__close" aria-label="إغلاق" @click="closeDownloadSheet">
                        <lucide-icon name="X" :size="20" />
                    </button>
                </div>
                <div class="g10c-sheet__actions">
                    <a
                        v-for="book in downloadBooks"
                        :key="`sheet-${book.language}`"
                        :href="book.url"
                        class="g10c-btn"
                        :class="book.language === 'ar' ? 'g10c-btn--primary' : 'g10c-btn--secondary'"
                        :download="downloadFileName(book)"
                        @click="onSheetDownload(book)"
                    >
                        <lucide-icon name="Download" :size="18" aria-hidden="true" />
                        <span>{{ book.language === 'ar' ? 'الكتاب العربي PDF' : 'English Book PDF' }}</span>
                    </a>
                    <a
                        v-for="book in downloadBooks"
                        :key="`sheet-open-${book.language}`"
                        :href="book.url"
                        class="g10c-btn g10c-btn--ghost"
                        target="_blank"
                        rel="noopener noreferrer"
                        @click="onBookTrack(book.trackOpen, downloadSheetPlacement, book.url)"
                    >
                        <lucide-icon name="ExternalLink" :size="18" aria-hidden="true" />
                        <span>{{ book.language === 'ar' ? 'فتح العربي' : 'Open English' }}</span>
                    </a>
                </div>
            </div>
        </div>

        <!-- Source image modal -->
        <div
            v-if="activeSource"
            class="g10c-modal"
            role="dialog"
            aria-modal="true"
            :aria-label="activeSource.label"
            @keydown.esc.prevent="closeSourceModal"
        >
            <button type="button" class="g10c-modal__backdrop" aria-label="إغلاق" @click="closeSourceModal" />
            <div class="g10c-modal__panel" ref="sourceModal">
                <button type="button" class="g10c-modal__close" aria-label="إغلاق" @click="closeSourceModal">
                    <lucide-icon name="X" :size="22" />
                </button>
                <img
                    :src="activeSource.src"
                    :alt="activeSource.alt"
                    :title="activeSource.title || activeSource.label"
                    :width="activeSource.width || undefined"
                    :height="activeSource.height || undefined"
                    class="g10c-modal__img"
                />
                <p class="g10c-modal__caption">{{ activeSource.caption || activeSource.label }}</p>
            </div>
        </div>
    </main>
</template>

<script>
import LucideIcon from '@/components/welcome/landing/LucideIcon.vue';
import CurriculumFaqAccordion from './CurriculumFaqAccordion.vue';
import CurriculumMobileSticky from './CurriculumMobileSticky.vue';
import CurriculumUnitBlock from './CurriculumUnitBlock.vue';
import {
    grade10CurriculumConfig,
    heroChips,
    heroFlow,
    summaryGrid,
    statusGuide,
    benefits,
    studySteps,
    courseFeatures,
    theoryColumns,
    downloadBooks,
    sourceDocuments,
    curriculumDistributionMaps,
    curriculumUnits,
    termOneWeeks,
    termOneSummary,
    termTwoWeeks,
    termTwoSummary,
    notScheduledByTerm,
    curriculumFaqItems,
    FILTER_OPTIONS,
} from './grade10-curriculum-data';
import { trackSeoCta, trackSeoEvent, trackSeoPageView } from '@/tracking/seo-events';

export default {
    name: 'Grade10CurriculumLanding',
    components: {
        LucideIcon,
        CurriculumFaqAccordion,
        CurriculumMobileSticky,
        CurriculumUnitBlock,
    },
    data() {
        return {
            config: grade10CurriculumConfig,
            teacherImage: require('@/assets/welcome/ghonaim-portrait.webp'),
            heroChips,
            heroFlow,
            summaryGrid,
            statusGuide,
            benefits,
            studySteps,
            courseFeatures,
            theoryColumns,
            downloadBooks,
            sourceDocuments,
            units: curriculumUnits,
            termOneWeeks,
            termOneSummary,
            termTwoWeeks,
            termTwoSummary,
            notScheduledByTerm,
            faqItems: curriculumFaqItems,
            filterOptions: FILTER_OPTIONS,
            activeFilterId: 'all',
            activeFilterField: null,
            activeFilterValue: null,
            scrollMilestones: { half: false, almost: false },
            downloadSheetOpen: false,
            downloadSheetPlacement: 'downloads',
            activeSource: null,
            previouslyFocused: null,
        };
    },
    computed: {
        formattedReviewDate() {
            const raw = this.config.lastReviewedDate;
            if (!raw) return '';
            const [y, m, d] = raw.split('-');
            return `${d}/${m}/${y}`;
        },
        termOneMap() {
            return curriculumDistributionMaps.find((map) => map.term === 1) || null;
        },
        termTwoMap() {
            return curriculumDistributionMaps.find((map) => map.term === 2) || null;
        },
    },
    mounted() {
        trackSeoPageView('grade10_curriculum_page_view');
        this.applyQueryFilters();
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('keydown', this.onGlobalKeydown);
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('keydown', this.onGlobalKeydown);
        this.unlockBodyScroll();
    },
    methods: {
        applyQueryFilters() {
            const status = this.$route && this.$route.query ? this.$route.query.status : null;
            const type = this.$route && this.$route.query ? this.$route.query.type : null;

            if (status) {
                const statusQuery = status === 'not-scheduled' ? 'not-scheduled' : status;
                const match = this.filterOptions.find(
                    (opt) => opt.field === 'status' && opt.query === statusQuery
                );
                if (match) {
                    this.activeFilterId = match.id;
                    this.activeFilterField = 'status';
                    this.activeFilterValue = match.id === 'not-in-distribution' ? 'not-in-distribution' : match.id;
                    return;
                }
            }

            if (type) {
                const match = this.filterOptions.find((opt) => opt.field === 'type' && opt.query === type);
                if (match) {
                    this.activeFilterId = match.id;
                    this.activeFilterField = 'type';
                    this.activeFilterValue = match.id;
                }
            }
        },
        setFilter(option) {
            this.activeFilterId = option.id;
            if (!option.query) {
                this.activeFilterField = null;
                this.activeFilterValue = null;
            } else {
                this.activeFilterField = option.field;
                this.activeFilterValue = option.id === 'not-in-distribution' ? 'not-in-distribution' : option.id;
            }
            trackSeoEvent('grade10_filter_change', {
                placement: 'filters',
                destination: option.id,
            });
        },
        unitMatchesFilter(unit) {
            if (!this.activeFilterField || !this.activeFilterValue) return true;
            const lessons = unit.lessons || [];
            if (this.activeFilterField === 'status') {
                return lessons.some((l) => l.status === this.activeFilterValue);
            }
            if (this.activeFilterField === 'type') {
                return lessons.some((l) => l.type === this.activeFilterValue);
            }
            return true;
        },
        lessonMatchesFilter(lesson) {
            if (!this.activeFilterField || !this.activeFilterValue) return true;
            if (this.activeFilterField === 'status') {
                return lesson.status === this.activeFilterValue;
            }
            if (this.activeFilterField === 'type') {
                return lesson.type === this.activeFilterValue;
            }
            return true;
        },
        downloadFileName(book) {
            return book.language === 'ar'
                ? 'grade-10-programming-ai-arabic-book-2025-2026.pdf'
                : 'grade-10-programming-ai-english-book-2025-2026.pdf';
        },
        onTrack(eventName, placement, destination) {
            trackSeoCta(eventName, placement, destination);
        },
        onBookTrack(eventName, placement, destination) {
            trackSeoEvent(eventName, { placement, destination });
        },
        onInternal(kind, placement) {
            if (kind === 'home') {
                trackSeoEvent('grade10_curriculum_page_view', { placement });
            }
        },
        onUnitNav(unit) {
            trackSeoEvent('grade10_unit_nav_click', {
                placement: 'toc',
                destination: `#${unit.id}`,
            });
        },
        onUnitOpen({ unitId, title }) {
            trackSeoEvent('grade10_unit_open', {
                placement: 'units',
                destination: unitId || title,
            });
        },
        onFaqOpen({ question }) {
            trackSeoEvent('grade10_faq_open', { placement: 'faq', destination: question });
        },
        openDownloadSheet(placement) {
            this.downloadSheetPlacement = placement || 'downloads';
            this.previouslyFocused = document.activeElement;
            this.downloadSheetOpen = true;
            this.lockBodyScroll();
            this.$nextTick(() => {
                const panel = this.$refs.downloadSheet;
                if (panel && panel.querySelector) {
                    const focusable = panel.querySelector('a, button');
                    if (focusable) focusable.focus();
                }
            });
            trackSeoEvent('grade10_download_sheet_open', { placement: this.downloadSheetPlacement });
        },
        closeDownloadSheet() {
            this.downloadSheetOpen = false;
            this.unlockBodyScroll();
            if (this.previouslyFocused && this.previouslyFocused.focus) {
                this.previouslyFocused.focus();
            }
        },
        onSheetDownload(book) {
            this.onBookTrack(book.trackDownload, this.downloadSheetPlacement, book.url);
            this.closeDownloadSheet();
        },
        openSourceModal(doc) {
            if (!doc || doc.pending || !doc.src) return;
            this.previouslyFocused = document.activeElement;
            this.activeSource = doc;
            this.lockBodyScroll();
            this.$nextTick(() => {
                const panel = this.$refs.sourceModal;
                if (panel) {
                    const closeBtn = panel.querySelector('button');
                    if (closeBtn) closeBtn.focus();
                }
            });
            trackSeoEvent('grade10_source_open', { placement: 'sources', destination: doc.id });
        },
        closeSourceModal() {
            this.activeSource = null;
            this.unlockBodyScroll();
            if (this.previouslyFocused && this.previouslyFocused.focus) {
                this.previouslyFocused.focus();
            }
        },
        onGlobalKeydown(event) {
            if (event.key !== 'Escape') return;
            if (this.activeSource) {
                this.closeSourceModal();
            } else if (this.downloadSheetOpen) {
                this.closeDownloadSheet();
            }
        },
        lockBodyScroll() {
            if (typeof document !== 'undefined') {
                document.documentElement.style.overflow = 'hidden';
            }
        },
        unlockBodyScroll() {
            if (typeof document !== 'undefined' && !this.downloadSheetOpen && !this.activeSource) {
                document.documentElement.style.overflow = '';
            }
        },
        onScroll() {
            const doc = document.documentElement;
            const max = doc.scrollHeight - window.innerHeight;
            if (max <= 0) return;
            const ratio = window.scrollY / max;
            if (!this.scrollMilestones.half && ratio >= 0.5) {
                this.scrollMilestones.half = true;
                trackSeoEvent('grade10_scroll_50');
            }
            if (!this.scrollMilestones.almost && ratio >= 0.9) {
                this.scrollMilestones.almost = true;
                trackSeoEvent('grade10_scroll_90');
            }
        },
    },
};
</script>
